// #2 Plano de Investigação (FP-CMP-010) — COMPARTILHADO entre o comitê.
//
// 🔴 A "OPÇÃO (c)" FOI INVERTIDA (operador, 2026-08-13 — ADR-0023). O cabeçalho
// anterior dizia: "captura EFÊMERA (...) nada é persistido (...) Conteúdo NUNCA sobe
// ao servidor". O plano vivia só no navegador de quem digitou, e era por isso que
// ninguém mais no comitê conseguia lê-lo — que é justamente o que se pediu para mudar.
//
// COMO A INVERSÃO NÃO VIROU VAZAMENTO: o que sobe é `ciphertext` + `nonce`. O JSON do
// formulário é cifrado NO BROWSER com a DEK do caso (AAD = caseId) antes do POST, e o
// servidor guarda bytes opacos — ele não tem a DEK (ADR-0002). O plano traz hipóteses,
// fontes e uma TABELA DE PESSOAS com nome, função e papel (denunciado, testemunhas);
// em claro seria nome de testemunha no banco.
//
// O PDF continua sendo montado no cliente, por `buildPlano` + `renderForm` — o que se
// persiste são os DADOS, não os bytes do PDF. Assim o documento sai sempre com o
// template atual, e o registro ocupa uma fração do espaço.
//
// Dois cards, como pedido: GERAR (o formulário de sempre) e GERADOS (o que o comitê
// já produziu, com download).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Field, Input, Textarea, dataHora } from '@aions/ui-primitives';
import { encryptPayload, decryptPayload } from '@aions/crypto';
import type { DenunciaState } from '@aions/core';
import { apiFetch } from '../lib/auth-client';
import { withCaseDek, type CaseDekAccess } from '../lib/case-crypto';
import { bytesToBase64, base64ToBytes } from '../lib/bytes';
import { renderForm } from '../lib/pdf/form-pdf';
import { buildPlano, type PlanoData, type PlanoPessoa } from '../lib/pdf/form-plano';
import type { FormInput, MemberInfo } from '../lib/pdf/forms-common';

/** Um plano já gerado, como vem da projeção do caso (conteúdo CIFRADO). */
export interface PlanoGerado {
  ulid: string;
  ciphertext: string;
  nonce: string;
  criadoPor: string;
  criadoAtEpochMs: number;
}

interface Props {
  caseId: string;
  categoria: string;
  createdWeek: string;
  state: DenunciaState;
  /** Planos já gerados pelo comitê. Opcional: projeção nova — ausente ⇒ o card
   *  "Planos gerados" mostra o vazio, em vez de a aba quebrar. */
  planos?: ReadonlyArray<PlanoGerado>;
  /** Acesso à DEK do caso. Sem ele (`status !== 'ok'`) não há como cifrar nem
   *  decifrar: o card de gerar avisa e o de baixar não oferece o botão. */
  caseCrypto: CaseDekAccess;
  /** id→e-mail, para dizer QUEM gerou cada plano em vez do ULID opaco. */
  members?: ReadonlyArray<{ id: string; email: string }> | null;
  /** Recarrega o caso após gerar — o novo plano precisa aparecer na lista. */
  onGerado?: () => void;
}

const FONTE_KEYS = [
  'documentosFisicos',
  'registrosDigitais',
  'logsAcessos',
  'imagensGravacoes',
  'anexosDenunciante',
] as const;

const emptyFontes: PlanoData['fontes'] = {
  documentosFisicos: false,
  registrosDigitais: false,
  logsAcessos: false,
  imagensGravacoes: false,
  anexosDenunciante: false,
  outros: false,
  outrosTexto: '',
};

const emptyPessoa: PlanoPessoa = { nomeFuncao: '', papel: '', statusOitiva: '' };

export function PlanoPanel({
  caseId,
  categoria,
  createdWeek,
  state,
  planos,
  caseCrypto,
  members,
  onGerado,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [hipoteses, setHipoteses] = useState('');
  const [fontes, setFontes] = useState<PlanoData['fontes']>(emptyFontes);
  const [pessoas, setPessoas] = useState<PlanoPessoa[]>([{ ...emptyPessoa }]);
  const [recursos, setRecursos] = useState('');
  const [inicioPrevisto, setInicioPrevisto] = useState('');
  const [conclusaoPrevista, setConclusaoPrevista] = useState('');
  // Qual painel esta ABERTO. `null` = os dois cards fechados, que e o estado
  // inicial: os cabecalhos ja informam (o de gerados traz a contagem), e abrir
  // e um ato deliberado. Mutuamente exclusivo — abrir um fecha o outro.
  const [aberto, setAberto] = useState<'gerar' | 'gerados' | null>(null);
  const [gerando, setGerando] = useState(false);
  const [baixando, setBaixando] = useState<string | null>(null);
  const [erro, setErro] = useState<'sem_chave' | 'falha_compartilhar' | 'falha_decrypt' | null>(
    null,
  );

  const setPessoa = (i: number, patch: Partial<PlanoPessoa>): void => {
    setPessoas((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  };

  const handleGerar = async (): Promise<void> => {
    setGerando(true);
    try {
      // Fetch lazy de membros (para o "Responsável pela apuração" na seção 2).
      let members: ReadonlyArray<MemberInfo> = [];
      const resp = await apiFetch(`/api/comite/cases/${caseId}/members`);
      if (resp.ok) {
        const body = (await resp.json()) as { members: MemberInfo[] };
        members = body.members;
      }
      const input: FormInput = { caseId, categoria, createdWeek, state, attachments: [], members };
      const plano: PlanoData = {
        hipoteses,
        fontes,
        pessoas,
        recursos,
        inicioPrevisto,
        conclusaoPrevista,
        dataElaboracao: Date.now(),
      };
      // 1) O PDF sai na hora, como antes — quem gerou não perde o documento se o
      //    POST falhar.
      baixarPdf(buildPlano(input, plano));

      // 2) E o plano é COMPARTILHADO: cifra o JSON do formulário sob a DEK do caso e
      //    manda. `withCaseDek` abre a DEK e a zera no fim — o servidor recebe bytes
      //    opacos, nunca os nomes das pessoas da tabela.
      //
      //    ⚠️ A cifra vem DEPOIS do download de propósito. Se ela falhar (KEK morta,
      //    rede), a pessoa já tem o PDF na mão e o erro é sobre COMPARTILHAR, não sobre
      //    gerar. O contrário faria uma falha de rede parecer que o plano não saiu.
      if (caseCrypto.status !== 'ok') {
        setErro('sem_chave');
        return;
      }
      const enc = await withCaseDek(caseCrypto, async (dek) =>
        encryptPayload(JSON.stringify(plano), dek, caseId),
      );
      const respPlano = await apiFetch(`/api/comite/cases/${caseId}/plano`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ciphertext: bytesToBase64(enc.ciphertext),
          nonce: bytesToBase64(enc.nonce),
        }),
      });
      if (!respPlano.ok) {
        setErro('falha_compartilhar');
        return;
      }
      setErro(null);
      onGerado?.();
    } catch {
      setErro('falha_compartilhar');
    } finally {
      setGerando(false);
    }
  };

  /** Dispara o download de um PDF já montado. Extraído porque agora há DOIS
   *  caminhos que baixam: gerar agora, e baixar um plano de outro membro. */
  function baixarPdf(doc: ReturnType<typeof buildPlano>, sufixo = ''): void {
    const url = URL.createObjectURL(renderForm(doc));
    const a = document.createElement('a');
    a.href = url;
    a.download = `plano-investigacao-${caseId.slice(0, 12)}${sufixo}.pdf`;
    a.click();
    // Revoga no próximo tick: revogar em seguida cancela o download em alguns
    // navegadores antes de ele começar.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }

  /**
   * Baixa um plano JÁ GERADO — decifra no browser e re-renderiza o PDF.
   *
   * Re-renderizar (em vez de guardar os bytes do PDF) é o que mantém o registro
   * pequeno e o documento sempre com o template atual. O custo é precisar do
   * `members` de novo para o cabeçalho, que é o mesmo fetch que o gerar já faz.
   */
  const handleBaixar = async (pl: PlanoGerado): Promise<void> => {
    if (caseCrypto.status !== 'ok') return;
    setBaixando(pl.ulid);
    setErro(null);
    try {
      const json = await withCaseDek(caseCrypto, async (dek) =>
        decryptPayload(base64ToBytes(pl.ciphertext), base64ToBytes(pl.nonce), dek, caseId),
      );
      const dados = JSON.parse(json) as PlanoData;
      let membrosPdf: ReadonlyArray<MemberInfo> = [];
      const rm = await apiFetch(`/api/comite/cases/${caseId}/members`);
      if (rm.ok) membrosPdf = ((await rm.json()) as { members: MemberInfo[] }).members;
      const input: FormInput = {
        caseId,
        categoria,
        createdWeek,
        state,
        attachments: [],
        members: membrosPdf,
      };
      baixarPdf(buildPlano(input, dados), `-${pl.ulid.slice(-13)}`);
    } catch {
      // Falha de decifra é o caso real aqui (KEK morta após F5). Dizer isso é o que
      // separa "relogue" de "o arquivo está corrompido".
      setErro('falha_decrypt');
    } finally {
      setBaixando(null);
    }
  };

  /**
   * Abre o painel pedido (ou fecha, se ja estava aberto).
   *
   * ⚠️ Limpa `erro` na troca de proposito. O mesmo estado carrega erro de
   * COMPARTILHAR (painel de gerar) e de DECIFRAR (painel de gerados); sem esta
   * limpeza, a mensagem de um vazaria para o outro e apontaria a acao errada.
   */
  const abrir = (qual: 'gerar' | 'gerados'): void => {
    setErro(null);
    setAberto((atual) => (atual === qual ? null : qual));
  };

  // O bloco de erro vive nos DOIS paineis: so um fica aberto por vez, entao
  // quem exibe e sempre aquele onde a acao aconteceu. Uma unica definicao —
  // duas copias divergiriam.
  const erroBloco =
    erro !== null ? (
      <p className="plano-erro" role="alert">
        {t(`plano.erro_${erro}`)}
      </p>
    ) : null;

  // Checkbox não tem primitiva no @aions/ui-primitives; alinhamos por token
  // de marca (accent-color) em vez de deixar o azul default do SO.
  const checkStyle = { accentColor: 'var(--primary)' };

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── CARD 1: GERAR PLANO ───────────────────────────────────────────────
          O cabeçalho é um <button>, não um <div> com onClick: `Card` renderiza uma
          div e sua prop `interactive` dá só hover/cursor — teclado e leitor de tela
          ficariam de fora. Com button, Tab/Enter/Espaço e o anúncio de
          expandido/recolhido vêm do próprio elemento. */}
      <Card padding="none" data-testid="plano-gerar">
        <button
          type="button"
          className="plano-acc__cab"
          aria-expanded={aberto === 'gerar'}
          aria-controls="painel-plano-gerar"
          onClick={() => {
            abrir('gerar');
          }}
        >
          <span className="plano-acc__texto">
            <span className="plano-acc__titulo">{t('plano.card_gerar_titulo')}</span>
            <span className="plano-acc__sub">{t('plano.intro')}</span>
          </span>
          <span className="plano-acc__seta" aria-hidden="true">
            ›
          </span>
        </button>
        {/* ⚠️ `hidden` em vez de render condicional, e o motivo é ARIA, não estado.
            `aria-controls` acima aponta para o id deste elemento; com
            `{aberto === 'gerar' && <painel/>}` o elemento não existe enquanto
            fechado, e a referência fica pendurada — o leitor de tela anuncia um
            controle que governa nada.

            🔴 CORREÇÃO de uma justificativa anterior: dizia-se aqui que `hidden`
            protegia o formulário de perder o que foi digitado. É FALSO, e foi
            medido — `hipoteses`, `fontes`, `pessoas` e o cronograma são `useState`
            DESTE componente, não do painel; desmontar a div não os toca. O dado
            sobrevive nas duas implementações.

            Por isso o `display: flex` fica no filho: um display no próprio
            elemento `hidden` venceria o atributo e o painel nunca fecharia. */}
        <div id="painel-plano-gerar" className="plano-acc__corpo" hidden={aberto !== 'gerar'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 3. Hipóteses */}
            <Field label={t('plano.hipoteses')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  value={hipoteses}
                  onChange={(e) => setHipoteses(e.target.value)}
                />
              )}
            </Field>

            {/* 4. Fontes — checkboxes */}
            <fieldset
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm, 8px)',
                background: 'var(--surface-inset)',
                padding: 12,
              }}
            >
              <legend style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t('plano.fontes')}</legend>
              {FONTE_KEYS.map((k) => (
                <label
                  key={k}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '6px 0',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    style={{ ...checkStyle, width: 16, height: 16, flexShrink: 0 }}
                    checked={fontes[k]}
                    onChange={(e) => setFontes((prev) => ({ ...prev, [k]: e.target.checked }))}
                  />
                  <span>{t(`plano.fonte_${k}`)}</span>
                </label>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0' }}>
                <input
                  type="checkbox"
                  style={checkStyle}
                  checked={fontes.outros}
                  onChange={(e) => setFontes((prev) => ({ ...prev, outros: e.target.checked }))}
                />
                {t('plano.fonte_outros')}
                <Input
                  type="text"
                  style={{ flex: 1 }}
                  value={fontes.outrosTexto}
                  disabled={!fontes.outros}
                  onChange={(e) => setFontes((prev) => ({ ...prev, outrosTexto: e.target.value }))}
                />
              </label>
            </fieldset>

            {/* 5. Pessoas a ouvir — tabela adicionável */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="cf-field__label">{t('plano.pessoas')}</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span
                  style={{
                    flex: 2,
                    minWidth: 140,
                    fontSize: '0.75rem',
                    color: 'var(--color-text-muted)',
                    fontWeight: 500,
                  }}
                >
                  Nome / Função
                </span>
                <span
                  style={{
                    flex: 2,
                    minWidth: 120,
                    fontSize: '0.75rem',
                    color: 'var(--color-text-muted)',
                    fontWeight: 500,
                  }}
                >
                  Papel na investigação
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 100,
                    fontSize: '0.75rem',
                    color: 'var(--color-text-muted)',
                    fontWeight: 500,
                  }}
                >
                  Status
                </span>
              </div>
              {pessoas.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                  <Input
                    style={{ flex: 2, minWidth: 140 }}
                    placeholder={t('plano.pessoa_nome')}
                    value={p.nomeFuncao}
                    onChange={(e) => setPessoa(i, { nomeFuncao: e.target.value })}
                  />
                  <Input
                    style={{ flex: 2, minWidth: 120 }}
                    placeholder={t('plano.pessoa_papel')}
                    value={p.papel}
                    onChange={(e) => setPessoa(i, { papel: e.target.value })}
                  />
                  <Input
                    style={{ flex: 1, minWidth: 100 }}
                    placeholder={t('plano.pessoa_status')}
                    value={p.statusOitiva}
                    onChange={(e) => setPessoa(i, { statusOitiva: e.target.value })}
                  />
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPessoas((prev) => [...prev, { ...emptyPessoa }])}
              >
                {t('plano.add_pessoa')}
              </Button>
            </div>

            {/* 6. Recursos */}
            <Field label={t('plano.recursos')}>
              {({ id }) => (
                <Textarea id={id} value={recursos} onChange={(e) => setRecursos(e.target.value)} />
              )}
            </Field>

            {/* 8. Cronograma */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Field label={t('plano.inicio')}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="text"
                    placeholder="dd/mm/aaaa"
                    style={{ width: 160 }}
                    value={inicioPrevisto}
                    onChange={(e) => setInicioPrevisto(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('plano.conclusao')}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="text"
                    placeholder="dd/mm/aaaa"
                    style={{ width: 160 }}
                    value={conclusaoPrevista}
                    onChange={(e) => setConclusaoPrevista(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <div>
              <Button
                variant="primary"
                disabled={gerando}
                onClick={() => {
                  void handleGerar();
                }}
              >
                {gerando ? t('plano.gerando') : t('plano.gerar')}
              </Button>

              {/* Diz o MECANISMO no momento do ato: o PDF é montado aqui e o
                conteúdo é cifrado antes de ser compartilhado. */}
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginTop: 8 }}>
                {t('plano.efemero')}
              </p>

              {/* Erro do COMPARTILHAMENTO, separado do download. A pessoa já tem o PDF
                quando isto aparece — a frase precisa dizer que o que falhou foi
                compartilhar, senão ela gera de novo achando que não saiu. */}
              {erroBloco}
            </div>
          </div>
        </div>
      </Card>

      {/* ── CARD 2: PLANOS GERADOS ────────────────────────────────────────────
          O que o comitê já produziu, com download. É a metade que a "Opção (c)"
          impedia de existir: sem persistência, cada plano morria no navegador de
          quem digitou e ninguém mais o via.

          ⚠️ O conteúdo NÃO é exibido aqui, só baixado. Mostrar hipóteses e a tabela
          de pessoas numa lista significaria decifrar TODOS os planos ao abrir a aba —
          trabalho de cripto por item, e teor sensível na tela sem ninguém ter pedido.
          Decifra sob demanda, no clique. */}
      <Card padding="none" data-testid="plano-gerados">
        <button
          type="button"
          className="plano-acc__cab"
          aria-expanded={aberto === 'gerados'}
          aria-controls="painel-plano-gerados"
          onClick={() => {
            abrir('gerados');
          }}
        >
          <span className="plano-acc__texto">
            {/* A contagem fica no CABEÇALHO de propósito: é o que alguém precisa
                saber sem abrir — se há plano no caso, e quantos. */}
            <span className="plano-acc__titulo">
              {t('plano.card_gerados_titulo', { count: planos?.length ?? 0 })}
            </span>
            <span className="plano-acc__sub">{t('plano.card_gerados_sub')}</span>
          </span>
          <span className="plano-acc__seta" aria-hidden="true">
            ›
          </span>
        </button>
        <div id="painel-plano-gerados" className="plano-acc__corpo" hidden={aberto !== 'gerados'}>
          {(planos?.length ?? 0) === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              {t('plano.gerados_vazio')}
            </p>
          ) : (
            <ul className="plano-lista">
              {[...(planos ?? [])]
                // Mais RECENTE primeiro: o plano vigente é o último elaborado, e é ele
                // que alguém abrindo a aba quer. A ordem de append é cronológica.
                .sort((x, y) => y.criadoAtEpochMs - x.criadoAtEpochMs)
                .map((pl) => (
                  <li key={pl.ulid}>
                    <span className="plano-lista__quem">
                      {members?.find((m) => m.id === pl.criadoPor)?.email ?? pl.criadoPor}
                    </span>
                    <span className="plano-lista__quando">{dataHora(pl.criadoAtEpochMs)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={baixando === pl.ulid}
                      disabled={caseCrypto.status !== 'ok'}
                      onClick={() => {
                        void handleBaixar(pl);
                      }}
                    >
                      {t('plano.baixar')}
                    </Button>
                  </li>
                ))}
            </ul>
          )}
          {/* Erro de DECIFRA no download aparece aqui, no painel onde o clique
            aconteceu — `abrir` limpa o estado ao trocar de card. */}
          {erroBloco}
          {/* A afirmação é literal: o servidor guarda ciphertext e não tem a DEK. */}
          <p className="plano-nota">{t('plano.gerados_nota')}</p>
        </div>
      </Card>
    </div>
  );
}
